'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  acceptMarketplaceSellerTerms,
  getConnectStatus,
  getSellerState,
  startConnectOnboarding,
  type SellerCapabilities,
} from '@/platform/vendor/api';
import { ConnectSetupChecklist } from '@/platform/vendor/ConnectSetupChecklist';
import { MarketplaceSellerTermsAcceptance } from '@/platform/legal/MarketplaceSellerTermsAcceptance';
import { saveHaulSellResume, type HaulSellResume } from '@/platform/vendor/sellerResume';

export type SellerSetupChecklistProps = {
  sellerUserId: string;
  compact?: boolean;
  resume?: HaulSellResume | null;
  onReady?: () => void;
  showStorefrontUpgrade?: boolean;
};

export function SellerSetupChecklist({
  sellerUserId,
  compact,
  resume,
  onReady,
  showStorefrontUpgrade = !compact,
}: SellerSetupChecklistProps) {
  const [caps, setCaps] = useState<SellerCapabilities | null>(null);
  const [termsChecked, setTermsChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [connectRefreshKey, setConnectRefreshKey] = useState(0);

  async function refresh() {
    try {
      const state = await getSellerState();
      setCaps(state);
      if (state.canListFinds) onReady?.();
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load seller status');
    }
  }

  useEffect(() => {
    void refresh();
  }, [sellerUserId]);

  async function acceptTerms() {
    if (!termsChecked) {
      setError('Accept the marketplace seller terms to continue.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await acceptMarketplaceSellerTerms();
      await refresh();
      setConnectRefreshKey((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not accept terms');
    } finally {
      setSubmitting(false);
    }
  }

  async function continueToPaymentSetup() {
    if (resume) saveHaulSellResume(resume);
    setSubmitting(true);
    setError('');
    try {
      const connect = await startConnectOnboarding(sellerUserId);
      window.location.href = connect.onboardingUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start payment setup');
      setSubmitting(false);
    }
  }

  async function refreshConnect() {
    try {
      await getConnectStatus(sellerUserId);
      setConnectRefreshKey((n) => n + 1);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not refresh payment setup');
    }
  }

  if (!caps) {
    return <p className="text-muted">Loading seller setup…</p>;
  }

  if (caps.canListFinds && compact) {
    return <p className="text-muted">Payment setup complete. Enter your listing details below.</p>;
  }

  const mark = (done: boolean) => (done ? '✓' : '—');

  return (
    <section className={`seller-setup-checklist${compact ? ' seller-setup-checklist--compact' : ''}`}>
      <ol className="vendor-onboarding__checklist">
        <li className={caps.termsAccepted ? 'done' : ''}>
          Site terms {mark(caps.termsAccepted)}
          {!caps.termsAccepted && (
            <>
              {' '}
              — <Link href="/legal/terms/">Review site terms</Link>
            </>
          )}
        </li>
        <li className={caps.marketplaceTermsAccepted ? 'done' : ''}>
          Marketplace seller terms {mark(caps.marketplaceTermsAccepted)}
        </li>
        <li className={caps.connectOnboardingComplete ? 'done' : ''}>
          Stripe Connect payouts {mark(caps.connectOnboardingComplete)}
        </li>
      </ol>
      {!caps.marketplaceTermsAccepted ? (
        <>
          <MarketplaceSellerTermsAcceptance
        id="seller-setup-marketplace-terms"
        checked={termsChecked}
        onChange={setTermsChecked}
        disabled={submitting}
      />
          <p>
            <button type="button" className="btn btn-secondary" disabled={submitting || !caps.termsAccepted} onClick={() => void acceptTerms()}>
              {submitting ? 'Saving…' : 'Accept marketplace terms'}
            </button>
          </p>
          {!caps.termsAccepted && <p className="text-muted">Accept site terms before marketplace seller terms.</p>}
        </>
      ) : (
        <>
          <ConnectSetupChecklist vendorUserId={sellerUserId} refreshKey={connectRefreshKey} />
          {!caps.connectOnboardingComplete && (
            <p>
              <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => void continueToPaymentSetup()}>
                {submitting ? 'Starting…' : 'Continue to payment setup'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => void refreshConnect()}>
                Refresh status
              </button>
            </p>
          )}
        </>
      )}
      {showStorefrontUpgrade && caps.canListFinds && !caps.canManageCatalog && (
        <p className="text-muted">
          Need a full storefront with catalog and shipping? Apply from the Overview tab.
        </p>
      )}
      {error && <p className="form-error">{error}</p>}
    </section>
  );
}
